import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { apiService } from "../../services/api";
import { Floor, Shop, Terminal, RouteEntity } from "../../types";
import styles from "./MallMap.module.css";
import RouteQRModal from "../RouteQRModal/RouteQRModal";

interface MallMapProps {
  onShopClick?: (shop: Shop) => void;
  onClearSelection?: () => void;
  selectedShop?: Shop | null;
}

const DEFAULT_MAP_SCALE = (() => {
  const raw = process.env.REACT_APP_MAP_SCALE;
  const parsed =
    typeof raw === "string" && raw.trim().length > 0 ? Number(raw) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
})();

export default function MallMap({
  onShopClick,
  onClearSelection,
  selectedShop,
}: MallMapProps) {
  const {
    state,
    setCurrentFloor,
    setSelectedShop,
    setActiveRoutePoints,
    setSelectedShopReload,
    setCurrentFloorReload,
  } = useApp();
  const location = useLocation();

  // Debug: log selectedShop changes
  useEffect(() => {
    if (selectedShop) {
      console.log(
        "Selected shop in MallMap:",
        selectedShop.id,
        selectedShop.mapIconActiveUrl,
      );
    } else {
      console.log("No shop selected in MallMap");
    }
  }, [selectedShop]);

  // Debug: log route reload data
  useEffect(() => {
    console.log("Route reload data in MallMap:", {
      hasSelectedShopReload: !!state.selectedShopReload,
      selectedShopReloadId: state.selectedShopReload?.id,
      hasTerminalName: !!state.terminalName,
      terminalName: state.terminalName,
      currentFloorReload: state.currentFloorReload,
      shouldShowRepeatButton: !!(
        state.selectedShopReload && state.terminalName
      ),
    });
  }, [state.selectedShopReload, state.terminalName, state.currentFloorReload]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(DEFAULT_MAP_SCALE);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);
  const clickedOnShopRef = useRef(false);
  const [floorMap, setFloorMap] = useState<HTMLImageElement | null>(null);
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [shopIcons, setShopIcons] = useState<Map<string, HTMLImageElement>>(
    new Map(),
  );
  const [shopActiveIcons, setShopActiveIcons] = useState<
    Map<string, HTMLImageElement>
  >(new Map());
  const [terminalIcons, setTerminalIcons] = useState<
    Map<string, HTMLImageElement>
  >(new Map());
  const [terminalActiveIcons, setTerminalActiveIcons] = useState<
    Map<string, HTMLImageElement>
  >(new Map());
  const [currentTerminal, setCurrentTerminal] = useState<Terminal | null>(null);
  const [highlightTerminalId, setHighlightTerminalId] = useState<string | null>(
    null,
  );
  const [isHighlightVisible, setIsHighlightVisible] = useState(true);
  const potentialShopRef = useRef<Shop | null>(null);
  const pendingCenterRef = useRef<{ x: number; y: number } | null>(null);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const hitTestCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastCenteredRouteRef = useRef<string | null>(null);
  const isManualFloorChangeRef = useRef<boolean>(false);

  // Helper function to check if a pixel is opaque in an icon at given coordinates
  const isPixelOpaque = useCallback(
    (
      icon: HTMLImageElement,
      iconX: number,
      iconY: number,
      iconWidth: number,
      iconHeight: number,
      clickX: number,
      clickY: number,
    ): boolean => {
      try {
        // Convert click coordinates to icon-local coordinates
        const localX = clickX - iconX;
        const localY = clickY - iconY;

        // Check if click is within icon bounds
        if (
          localX < 0 ||
          localX >= iconWidth ||
          localY < 0 ||
          localY >= iconHeight
        ) {
          return false;
        }

        // Create a temporary canvas for hit testing if not exists
        if (!hitTestCanvasRef.current) {
          hitTestCanvasRef.current = document.createElement("canvas");
        }
        const hitCanvas = hitTestCanvasRef.current;
        const hitCtx = hitCanvas.getContext("2d", { willReadFrequently: true });
        if (!hitCtx) return true; // Fallback: assume opaque if can't check

        // Set canvas size to match icon
        hitCanvas.width = icon.width;
        hitCanvas.height = icon.height;

        // Draw icon on temporary canvas
        hitCtx.clearRect(0, 0, hitCanvas.width, hitCanvas.height);
        hitCtx.drawImage(icon, 0, 0);

        // Get pixel data at the click point (scaled to original icon size)
        const scaleX = iconWidth / icon.width;
        const scaleY = iconHeight / icon.height;
        const pixelX = Math.floor(localX / scaleX);
        const pixelY = Math.floor(localY / scaleY);

        // Clamp to valid range
        const clampedX = Math.max(0, Math.min(icon.width - 1, pixelX));
        const clampedY = Math.max(0, Math.min(icon.height - 1, pixelY));

        // Get pixel data
        const imageData = hitCtx.getImageData(clampedX, clampedY, 1, 1);
        const alpha = imageData.data[3]; // Alpha channel

        // Consider pixel opaque if alpha > 64 (25% opacity threshold - more tolerant)
        return alpha > 64;
      } catch (error) {
        // If any error occurs, assume pixel is opaque to allow clicking
        console.warn("Error checking pixel opacity:", error);
        return true;
      }
    },
    [],
  );

  // Clamp pan so the map can move exactly to the edges at any zoom level
  const clampPan = useCallback(
    (panX: number, panY: number, atScale?: number) => {
      const canvas = canvasRef.current;
      if (!canvas || !floorMap) return { x: panX, y: panY };

      const scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9,
      );
      const effScale = (atScale ?? scale) * scaleToFit;

      const scaledWidth = floorMap.width * effScale;
      const scaledHeight = floorMap.height * effScale;

      // Allowed pan range so that the image can be moved from left/top edge flush to right/bottom edge flush
      // mapX = (canvas.width - scaledWidth)/2 + pan.x must be within [0, canvas.width - scaledWidth]
      const halfSpanX = (canvas.width - scaledWidth) / 2;
      const halfSpanY = (canvas.height - scaledHeight) / 2;

      const edgeMargin = 1500; // allow +10px beyond each edge

      // Convert desired mapX range [2*halfSpanX - margin, margin] into pan range by subtracting halfSpan
      const minX = Math.min(halfSpanX - edgeMargin, -halfSpanX + edgeMargin);
      const maxX = Math.max(halfSpanX - edgeMargin, -halfSpanX + edgeMargin);
      const minY = Math.min(halfSpanY - edgeMargin, -halfSpanY + edgeMargin);
      const maxY = Math.max(halfSpanY - edgeMargin, -halfSpanY + edgeMargin);

      const clampedX = Math.max(minX, Math.min(maxX, panX));
      const clampedY = Math.max(minY, Math.min(maxY, panY));
      return { x: clampedX, y: clampedY };
    },
    [floorMap, scale],
  );

  const centerOnMapPoint = useCallback(
    (mapX: number, mapY: number, atScale?: number) => {
      const canvas = canvasRef.current;
      if (!canvas || !floorMap) return;
      const scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9,
      );
      const totalScale = (atScale ?? scale) * scaleToFit;
      const next = { x: -totalScale * mapX, y: -totalScale * mapY };
      setPan(clampPan(next.x, next.y, atScale));
    },
    [floorMap, scale, clampPan],
  );

  // Load floors
  useEffect(() => {
    const loadFloors = async () => {
      try {
        const floorsData = await apiService.getFloors();
        setFloors(floorsData);

        // Select floor based on currentFloor from context if available; fallback to first
        if (floorsData.length > 0) {
          const preferred = floorsData.find(
            (f) => f.number === state.currentFloor,
          );
          const initial = preferred || floorsData[0];
          setSelectedFloor(initial);
          if (state.currentFloor !== initial.number) {
            setCurrentFloor(initial.number);
          }
        }
      } catch (error) {
        console.error("Error loading floors:", error);
      } finally {
        setLoading(false);
      }
    };

    loadFloors();
  }, []);

  // Load shops for the selected floor
  useEffect(() => {
    if (!selectedFloor) return;

    const loadShops = async () => {
      try {
        const shopsData = await apiService.getShops({
          floor: selectedFloor.number,
        });
        // Keep a stable order by Z for consistent rendering (back to front)
        const sorted = [...shopsData].sort(
          (a, b) => Number(a.positionZ || 0) - Number(b.positionZ || 0),
        );
        setShops(sorted);
      } catch (error) {
        console.error("Error loading shops:", error);
      }
    };

    loadShops();
  }, [selectedFloor]);

  // Sync selected floor with context currentFloor when it changes
  useEffect(() => {
    if (!floors.length) return;
    const target = floors.find((f) => f.number === state.currentFloor);
    if (!target) return;
    if (selectedFloor?.id !== target.id) {
      setSelectedFloor(target);
    }
  }, [state.currentFloor, floors]);

  // Load terminals for the selected floor
  useEffect(() => {
    if (!selectedFloor) return;

    const loadTerminals = async () => {
      try {
        const terminalsData = await apiService.getTerminals({
          floor: selectedFloor.number,
          active: true,
        });
        setTerminals(terminalsData);
      } catch (error) {
        console.error("Error loading terminals:", error);
      }
    };

    loadTerminals();
  }, [selectedFloor]);

  // Load current terminal from URL when route is active
  // Load terminal by ?terminalName= (always, even без маршрута) и задать подсветку
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const terminalName = params.get("terminalName");
    if (!terminalName) {
      setCurrentTerminal(null);
      setHighlightTerminalId(null);
      return;
    }

    const loadTerminal = async () => {
      try {
        const terminals = await apiService.getTerminals({ active: true });
        const terminal = terminals.find(
          (t) =>
            t.name.trim().toLowerCase() === terminalName.trim().toLowerCase(),
        );
        setCurrentTerminal(terminal || null);
        setHighlightTerminalId(terminal?.id ?? null);
      } catch (e) {
        setCurrentTerminal(null);
        setHighlightTerminalId(null);
      }
    };

    loadTerminal();
  }, [location.search]);

  // Blink toggle for highlighted terminal (reset visible when id changes)
  useEffect(() => {
    if (!highlightTerminalId) {
      setIsHighlightVisible(true);
      return;
    }
    setIsHighlightVisible(true);
    const interval = setInterval(() => {
      setIsHighlightVisible((prev) => !prev);
    }, 600);
    return () => clearInterval(interval);
  }, [highlightTerminalId]);

  // Center on terminal when route is built (only once per new route)
  useEffect(() => {
    if (!state.activeRoutePoints || state.activeRoutePoints.length === 0) {
      lastCenteredRouteRef.current = null;
      return;
    }

    // Create a simple hash of the route to detect if it's a new route
    const routeHash = `${state.activeRoutePoints.length}-${
      state.activeRoutePoints[0]?.x
    }-${state.activeRoutePoints[0]?.y}-${
      state.activeRoutePoints[state.activeRoutePoints.length - 1]?.x
    }-${state.activeRoutePoints[state.activeRoutePoints.length - 1]?.y}`;

    // Only center if this is a new route (not already centered)
    if (lastCenteredRouteRef.current === routeHash) {
      return;
    }

    const centerOnTerminal = async () => {
      // Get terminalName from state or URL
      const terminalName =
        state.terminalName ||
        new URLSearchParams(location.search).get("terminalName");
      if (!terminalName) return;

      // Check manual floor change flag - if set, skip centering
      // But only if it was set recently (within last 500ms)
      const wasManualChange = isManualFloorChangeRef.current;
      if (wasManualChange) {
        // If user just manually changed floor, don't center
        return;
      }

      try {
        const allTerminals = await apiService.getTerminals({ active: true });
        const target = allTerminals.find(
          (t) =>
            t.name.trim().toLowerCase() === terminalName.trim().toLowerCase(),
        );
        if (!target) return;

        // Mark this route as centered BEFORE changing floor/centering
        lastCenteredRouteRef.current = routeHash;

        // При построении маршрута сбрасываем масштаб на "обычный"
        const normalScale = DEFAULT_MAP_SCALE;
        setScale(normalScale);

        // Map coordinates used in canvas drawing
        const mapX =
          (typeof target.positionX === "number"
            ? target.positionX
            : parseFloat(String(target.positionX))) * 100;
        const mapY =
          (typeof target.positionY === "number"
            ? target.positionY
            : parseFloat(String(target.positionY))) * 100;

        // Get current selected floor (may have changed)
        const currentSelectedFloor = floors.find(
          (f) => f.number === state.currentFloor,
        );

        // Only change floor if we're not on the correct floor AND user didn't manually change it
        if (currentSelectedFloor?.number !== target.floor && !wasManualChange) {
          const floor = floors.find((f) => f.number === target.floor);
          if (floor) {
            setSelectedFloor(floor);
            if (state.currentFloor !== floor.number)
              setCurrentFloor(floor.number);
            // Defer centering until floorMap for the new floor is ready
            // Store route hash with pending center so we can mark it as centered later
            pendingCenterRef.current = {
              x: mapX,
              y: mapY,
              scale: normalScale,
              routeHash: routeHash,
            } as any;
            return;
          }
        }

        // Same floor: center immediately
        // Use setTimeout to ensure floorMap is ready
        setTimeout(() => {
          // Double-check that user didn't manually change floor during the timeout
          if (!isManualFloorChangeRef.current) {
            centerOnMapPoint(mapX, mapY, normalScale);
          }
        }, 100);
      } catch (e) {
        // Silently ignore if API fails
      }
    };

    centerOnTerminal();
  }, [
    state.activeRoutePoints,
    state.terminalName,
    location.search,
    floors,
    state.currentFloor,
    setCurrentFloor,
    centerOnMapPoint,
  ]);

  // Load terminal icons (inactive and active)
  useEffect(() => {
    if (terminals.length === 0) {
      setTerminalIcons(new Map());
      setTerminalActiveIcons(new Map());
      return;
    }

    // Load inactive icons - update immediately when each loads
    terminals.forEach((terminal) => {
      if (!terminal.mapIconInactiveUrl) return;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setTerminalIcons((prev) => {
          const newMap = new Map(prev);
          newMap.set(terminal.id, img);
          return newMap;
        });
      };
      img.onerror = () => {
        console.error(
          `Failed to load inactive terminal icon for terminal ${terminal.id}`,
        );
      };
      img.src = `${process.env.REACT_APP_MEDIA_PATH}${terminal.mapIconInactiveUrl}`;
    });

    // Load active icons - update immediately when each loads
    terminals.forEach((terminal) => {
      if (!terminal.mapIconActiveUrl) return;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setTerminalActiveIcons((prev) => {
          const newMap = new Map(prev);
          newMap.set(terminal.id, img);
          return newMap;
        });
      };
      img.onerror = () => {
        console.error(
          `Failed to load active terminal icon for terminal ${terminal.id}`,
        );
      };
      img.src = `${process.env.REACT_APP_MEDIA_PATH}${terminal.mapIconActiveUrl}`;
    });
  }, [terminals]);

  // Load floor map image
  useEffect(() => {
    if (!selectedFloor) return;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setFloorMap(img);
    img.onerror = () => console.error("Failed to load floor map");
    img.src = `${process.env.REACT_APP_MEDIA_PATH}${selectedFloor.mapUrl}`;
  }, [selectedFloor]);

  // Load shop icons (inactive and active)
  useEffect(() => {
    if (shops.length === 0) {
      setShopIcons(new Map());
      setShopActiveIcons(new Map());
      return;
    }

    // Load inactive icons - update immediately when each loads
    shops.forEach((shop) => {
      if (!shop.mapIconInactiveUrl) return;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setShopIcons((prev) => {
          const newMap = new Map(prev);
          newMap.set(shop.id, img);
          return newMap;
        });
      };
      img.onerror = () => {
        console.error(`Failed to load inactive shop icon for shop ${shop.id}`);
      };
      img.src = `${process.env.REACT_APP_MEDIA_PATH}${shop.mapIconInactiveUrl}`;
    });

    // Load active icons - update immediately when each loads
    shops.forEach((shop) => {
      if (!shop.mapIconActiveUrl) return;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setShopActiveIcons((prev) => {
          const newMap = new Map(prev);
          newMap.set(shop.id, img);
          return newMap;
        });
      };
      img.onerror = () => {
        console.error(`Failed to load active shop icon for shop ${shop.id}`);
      };
      img.src = `${process.env.REACT_APP_MEDIA_PATH}${shop.mapIconActiveUrl}`;
    });
  }, [shops]);

  // Draw function
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Enable high-quality image smoothing for better rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    let scaleToFit = 1;
    let scaledWidth = 0;
    let scaledHeight = 0;
    let mapX = 0;
    let mapY = 0;

    // Draw floor map with zoom and pan
    if (floorMap) {
      // Save context
      ctx.save();

      // Calculate scale to fit map in canvas
      scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9, // Leave 10% margin on each side
      );

      scaledWidth = floorMap.width * scaleToFit * scale;
      scaledHeight = floorMap.height * scaleToFit * scale;

      // Center position with pan offset
      mapX = (canvas.width - scaledWidth) / 2 + pan.x;
      mapY = (canvas.height - scaledHeight) / 2 + pan.y;

      // Draw map with scaling and pan
      ctx.drawImage(floorMap, mapX, mapY, scaledWidth, scaledHeight);

      // Restore context
      ctx.restore();
    }

    // Draw shop icons
    if (floorMap && shopIcons.size > 0) {
      // Save context for icon drawing
      ctx.save();

      // Apply transformations to match map position and scale
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.translate(pan.x, pan.y);
      ctx.scale(scale * scaleToFit, scale * scaleToFit);

      // Draw each shop icon (already sorted back-to-front by positionZ)
      shops.forEach((shop) => {
        if (!shop.mapIconInactiveUrl) return;

        // Check if this shop is selected and has an active icon
        // Use state.selectedShop from context instead of prop to ensure consistency
        // Compare IDs as strings to handle different formats
        const isSelected =
          (state.selectedShop?.id !== undefined &&
            String(state.selectedShop.id) === String(shop.id)) ||
          (selectedShop?.id !== undefined &&
            String(selectedShop.id) === String(shop.id));
        let icon: HTMLImageElement | undefined;
        let usingActiveIcon = false;

        if (isSelected && shop.mapIconActiveUrl) {
          icon = shopActiveIcons.get(shop.id);
          if (icon) {
            usingActiveIcon = true;
          } else {
            // Fallback to inactive icon if active is not loaded yet
            icon = shopIcons.get(shop.id);
          }
        } else {
          icon = shopIcons.get(shop.id);
        }

        if (!icon) return;

        // // Debug log for selected shop
        // if (isSelected) {
        //   console.log(
        //     `Drawing shop ${shop.id}, using ${
        //       usingActiveIcon ? "ACTIVE" : "INACTIVE"
        //     } icon, has mapIconActiveUrl: ${!!shop.mapIconActiveUrl}, activeIcon loaded: ${!!shopActiveIcons.get(
        //       shop.id
        //     )}`
        //   );
        // }

        // Convert position values to numbers (they might be Decimal from Prisma)
        const posX =
          typeof shop.positionX === "number"
            ? shop.positionX
            : parseFloat(String(shop.positionX));
        const posY =
          typeof shop.positionY === "number"
            ? shop.positionY
            : parseFloat(String(shop.positionY));

        // Shop position in scaled coordinates (multiply by 100 as in ShopPositioner2D)
        const shopCanvasX = posX * 100;
        const shopCanvasY = posY * 100;

        // Convert and apply shop's scale
        const scaleX =
          typeof shop.scaleX === "number"
            ? shop.scaleX
            : parseFloat(String(shop.scaleX || 1));
        const scaleY =
          typeof shop.scaleY === "number"
            ? shop.scaleY
            : parseFloat(String(shop.scaleY || 1));
        const iconWidth = icon.width * scaleX;
        const iconHeight = icon.height * scaleY;

        // Draw the icon centered at shop position
        ctx.drawImage(
          icon,
          shopCanvasX - iconWidth / 2,
          shopCanvasY - iconHeight / 2,
          iconWidth,
          iconHeight,
        );
      });

      // Restore context
      ctx.restore();
    }

    // Draw terminals
    if (floorMap && terminals.length > 0 && terminalIcons.size > 0) {
      // Save context for terminal drawing
      ctx.save();

      // Apply transformations to match map position and scale
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.translate(pan.x, pan.y);
      ctx.scale(scale * scaleToFit, scale * scaleToFit);

      // Draw each terminal
      terminals.forEach((terminal) => {
        if (!terminal.isActive) return;
        if (!terminal.mapIconInactiveUrl) return;

        const isHighlighted =
          highlightTerminalId &&
          String(highlightTerminalId) === String(terminal.id);

        let icon: HTMLImageElement | undefined;
        if (isHighlighted) {
          const activeIcon = terminalActiveIcons.get(terminal.id);
          const inactiveIcon = terminalIcons.get(terminal.id);
          // Blink by toggling between active and inactive (or hide if only one)
          icon = isHighlightVisible
            ? activeIcon || inactiveIcon
            : inactiveIcon || activeIcon;
        } else {
          // Default: prefer active icon when available
          icon =
            terminalActiveIcons.get(terminal.id) ||
            terminalIcons.get(terminal.id);
        }

        if (!icon) return;

        // For highlighted terminal, blink by toggling visibility (alpha)
        const prevAlpha = ctx.globalAlpha;
        if (isHighlighted) {
          ctx.globalAlpha = isHighlightVisible ? 1 : 0.05;
        }

        // Convert position values to numbers
        const posX =
          typeof terminal.positionX === "number"
            ? terminal.positionX
            : parseFloat(String(terminal.positionX));
        const posY =
          typeof terminal.positionY === "number"
            ? terminal.positionY
            : parseFloat(String(terminal.positionY));

        // Terminal position in scaled coordinates (multiply by 100 as in shop positioning)
        const terminalCanvasX = posX * 100;
        const terminalCanvasY = posY * 100;

        // Convert and apply terminal's scale
        const scaleX =
          typeof terminal.scaleX === "number"
            ? terminal.scaleX
            : parseFloat(String(terminal.scaleX || 1));
        const scaleY =
          typeof terminal.scaleY === "number"
            ? terminal.scaleY
            : parseFloat(String(terminal.scaleY || 1));
        const iconWidth = icon.width * scaleX;
        const iconHeight = icon.height * scaleY;

        // Draw the terminal icon centered at terminal position
        ctx.drawImage(
          icon,
          terminalCanvasX - iconWidth / 2,
          terminalCanvasY - iconHeight / 2,
          iconWidth,
          iconHeight,
        );

        // Restore alpha if it was changed
        ctx.globalAlpha = prevAlpha;
      });

      // Restore context
      ctx.restore();
    }

    // Draw active route polyline if present (only the current floor segment)
    if (
      floorMap &&
      state.selectedShop &&
      state.activeRoutePoints &&
      state.activeRoutePoints.length > 0
    ) {
      // Save context for route drawing
      ctx.save();

      // Apply same map transform
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.translate(pan.x, pan.y);
      ctx.scale(scale * scaleToFit, scale * scaleToFit);

      const currentFloor = state.currentFloor;
      const hasFloorInfo = state.activeRoutePoints.some(
        (p: any) => typeof (p as any).floor === "number",
      );
      const routePoints = hasFloorInfo
        ? state.activeRoutePoints.filter(
            (p: any) => (p as any).floor === currentFloor,
          )
        : state.activeRoutePoints;

      if (routePoints.length > 0) {
        // Configure line rendering for smooth appearance on touchscreens
        const baseLineWidth = 3 / (scale * scaleToFit);
        // Use minimum 4 pixels width - larger width renders smoother on high DPI displays
        const lineWidth = Math.max(4, baseLineWidth);

        // Enable all smoothing and quality settings
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        // Configure line properties for smooth edges - CRITICAL for smooth rendering
        ctx.lineJoin = "round"; // Essential: rounds corners between segments
        ctx.lineCap = "round"; // Essential: rounds line endings
        ctx.miterLimit = 10; // Prevents sharp spikes at corners

        ctx.strokeStyle = "#64A696";
        ctx.lineWidth = lineWidth;

        // Build path
        ctx.beginPath();
        routePoints.forEach((p, idx) => {
          const px = p.x;
          const py = p.y;
          if (idx === 0) {
            ctx.moveTo(px, py);
          } else {
            ctx.lineTo(px, py);
          }
        });

        // Draw the line with all smoothing applied
        ctx.stroke();

        // Optional dots
        routePoints.forEach((p) => {
          ctx.fillStyle = "#64A696";
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4 / (scale * scaleToFit), 0, Math.PI * 2);
          ctx.fill();
        });
      }

      ctx.restore();
    }
  }, [
    floorMap,
    scale,
    pan,
    shops,
    shopIcons,
    shopActiveIcons,
    selectedShop,
    state.selectedShop,
    terminals,
    terminalIcons,
    terminalActiveIcons,
    highlightTerminalId,
    isHighlightVisible,
    state.activeRoutePoints,
    state.currentFloor,
  ]);

  // Update canvas size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      draw();
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [draw]);

  // Redraw when relevant state changes
  useEffect(() => {
    draw();
  }, [draw]);

  // Redraw when floorMap loads
  useEffect(() => {
    draw();
    // Apply pending center (e.g., after floor switch) when floorMap becomes available
    // Only if it's not a manual floor change
    if (floorMap && pendingCenterRef.current) {
      if (isManualFloorChangeRef.current) {
        // Clear pending center if user manually changed floor
        pendingCenterRef.current = null;
      } else {
        const pending = pendingCenterRef.current as any;
        const { x, y, routeHash, scale: pendingScale } = pending;
        if (typeof pendingScale === "number") {
          setScale(pendingScale);
        }
        centerOnMapPoint(
          x,
          y,
          typeof pendingScale === "number" ? pendingScale : undefined,
        );
        // Mark route as centered if routeHash was provided
        if (routeHash) {
          lastCenteredRouteRef.current = routeHash;
        }
        pendingCenterRef.current = null;
      }
    }
  }, [floorMap, draw, centerOnMapPoint]);

  // Redraw when selectedShop or icons change
  useEffect(() => {
    draw();
  }, [selectedShop, state.selectedShop, shopActiveIcons, draw]);

  // Redraw when terminals or terminal icons change
  useEffect(() => {
    draw();
  }, [terminals, terminalIcons, terminalActiveIcons, draw]);

  // Prevent default wheel behavior
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheelCapture = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Call our zoom handler
      const rect = canvas.getBoundingClientRect();
      // In accessibility mode, the parent container has transform: scale(0.5)
      // So we need to adjust coordinates by multiplying by 2
      const accessibilityScale = state.isAccessibilityMode ? 2 : 1;
      const mouseX = (e.clientX - rect.left) * accessibilityScale;
      const mouseY = (e.clientY - rect.top) * accessibilityScale;

      const delta = e.deltaY > 0 ? 0.9 : 1.1;

      setScale((prev) => {
        const newScale = Math.max(0.5, Math.min(5, prev * delta));

        // Calculate pan adjustment to zoom towards mouse position
        const scaleToFit = Math.min(
          canvas.width / (floorMap?.width || 1),
          canvas.height / (floorMap?.height || 1),
          0.9,
        );

        // Point on image before zoom
        const imageX =
          (mouseX - canvas.width / 2 - pan.x) / (scaleToFit * prev);
        const imageY =
          (mouseY - canvas.height / 2 - pan.y) / (scaleToFit * prev);

        // Adjust pan to keep point under mouse fixed
        setPan((prevPan) => {
          const newPanX = prevPan.x - imageX * scaleToFit * (newScale - prev);
          const newPanY = prevPan.y - imageY * scaleToFit * (newScale - prev);
          return clampPan(newPanX, newPanY, newScale);
        });

        return newScale;
      });
    };

    canvas.addEventListener("wheel", handleWheelCapture, {
      passive: false,
      capture: true,
    });

    return () => {
      canvas.removeEventListener("wheel", handleWheelCapture, {
        capture: true,
      });
    };
  }, [floorMap, pan, clampPan, state.isAccessibilityMode]);

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    const rect = canvas.getBoundingClientRect();
    // In accessibility mode, the parent container has transform: scale(0.5)
    // So we need to adjust coordinates by multiplying by 2
    const accessibilityScale = state.isAccessibilityMode ? 2 : 1;
    const mouseX = (e.clientX - rect.left) * accessibilityScale;
    const mouseY = (e.clientY - rect.top) * accessibilityScale;

    // Check if clicking on a shop icon
    const scaleToFit = Math.min(
      canvas.width / floorMap.width,
      canvas.height / floorMap.height,
      0.9,
    );
    const totalScale = scale * scaleToFit;

    // Convert mouse position to map coordinates
    const mapX = (mouseX - canvas.width / 2 - pan.x) / totalScale;
    const mapY = (mouseY - canvas.height / 2 - pan.y) / totalScale;

    // Check each shop, starting from topmost (front to back) so overlaps click correctly
    // Sort by positionZ descending (highest Z first) to check topmost shops first
    const sortedShops = [...shops].sort(
      (a, b) => Number(b.positionZ || 0) - Number(a.positionZ || 0),
    );

    for (const shop of sortedShops) {
      if (!shop.mapIconInactiveUrl) continue;

      const icon = shopIcons.get(shop.id);
      if (!icon) continue;

      // Convert position values to numbers
      const posX =
        typeof shop.positionX === "number"
          ? shop.positionX
          : parseFloat(String(shop.positionX));
      const posY =
        typeof shop.positionY === "number"
          ? shop.positionY
          : parseFloat(String(shop.positionY));

      // Shop position in scaled coordinates
      const shopCanvasX = posX * 100;
      const shopCanvasY = posY * 100;

      // Icon size
      const scaleX =
        typeof shop.scaleX === "number"
          ? shop.scaleX
          : parseFloat(String(shop.scaleX || 1));
      const scaleY =
        typeof shop.scaleY === "number"
          ? shop.scaleY
          : parseFloat(String(shop.scaleY || 1));
      const iconWidth = icon.width * scaleX;
      const iconHeight = icon.height * scaleY;

      // Check if click is within icon bounds (rectangular check)
      // Add a small tolerance (5% of icon size) to make clicking easier
      const tolerance = Math.max(iconWidth, iconHeight) * 0.05;
      const halfWidth = iconWidth / 2 + tolerance;
      const halfHeight = iconHeight / 2 + tolerance;
      const left = shopCanvasX - halfWidth;
      const right = shopCanvasX + halfWidth;
      const top = shopCanvasY - halfHeight;
      const bottom = shopCanvasY + halfHeight;

      if (mapX >= left && mapX <= right && mapY >= top && mapY <= bottom) {
        // Check if the pixel at click position is opaque (not transparent)
        // But allow clicking even if check fails (fallback to rectangular area)
        const iconX = shopCanvasX - iconWidth / 2;
        const iconY = shopCanvasY - iconHeight / 2;
        let isOpaque = true; // Default to true

        try {
          isOpaque = isPixelOpaque(
            icon,
            iconX,
            iconY,
            iconWidth,
            iconHeight,
            mapX,
            mapY,
          );
        } catch (error) {
          // If opacity check fails, assume opaque to allow clicking
          isOpaque = true;
        }

        if (isOpaque) {
          // Pressed on shop icon: mark as potential click, allow drag-to-pan if moved
          clickedOnShopRef.current = true;
          potentialShopRef.current = shop;
          hasMovedRef.current = false;
          setIsPanning(true);
          setLastMousePos({ x: e.clientX, y: e.clientY });
          return;
        }
      }
    }

    // No shop clicked, start panning (don't clear selection yet)
    clickedOnShopRef.current = false;
    potentialShopRef.current = null;
    hasMovedRef.current = false;
    setIsPanning(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isPanning) {
      const deltaX = e.clientX - lastMousePos.x;
      const deltaY = e.clientY - lastMousePos.y;
      // Check if moved significantly (more than 3 pixels to avoid accidental triggers)
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        hasMovedRef.current = true;
      }
      setPan((prev) => clampPan(prev.x + deltaX, prev.y + deltaY));
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    // If mouse up without movement on a shop, trigger onShopClick
    if (!hasMovedRef.current && potentialShopRef.current && onShopClick) {
      onShopClick(potentialShopRef.current);
    } else if (
      !hasMovedRef.current &&
      !clickedOnShopRef.current &&
      onClearSelection
    ) {
      // Only clear selection if it was a click on empty space
      onClearSelection();
    }
    setIsPanning(false);
    hasMovedRef.current = false;
    clickedOnShopRef.current = false;
    potentialShopRef.current = null;
  };

  // Handle mouse leave without clearing selection
  const handleMouseLeave = () => {
    setIsPanning(false);
    hasMovedRef.current = false;
    clickedOnShopRef.current = false;
    potentialShopRef.current = null;
  };

  // Handle wheel zoom
  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    const rect = canvas.getBoundingClientRect();
    // In accessibility mode, the parent container has transform: scale(0.5)
    // So we need to adjust coordinates by multiplying by 2
    const accessibilityScale = state.isAccessibilityMode ? 2 : 1;
    const mouseX = (e.clientX - rect.left) * accessibilityScale;
    const mouseY = (e.clientY - rect.top) * accessibilityScale;

    const delta = e.deltaY > 0 ? 0.9 : 1.1;

    setScale((prev) => {
      const newScale = Math.max(0.5, Math.min(5, prev * delta));

      // Calculate pan adjustment to zoom towards mouse position
      const scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9,
      );

      // Point on image before zoom
      const imageX = (mouseX - canvas.width / 2 - pan.x) / (scaleToFit * prev);
      const imageY = (mouseY - canvas.height / 2 - pan.y) / (scaleToFit * prev);

      // Adjust pan to keep point under mouse fixed
      setPan((prevPan) => {
        const newPanX = prevPan.x - imageX * scaleToFit * (newScale - prev);
        const newPanY = prevPan.y - imageY * scaleToFit * (newScale - prev);
        return clampPan(newPanX, newPanY, newScale);
      });

      return newScale;
    });
  };

  // Handle touch events for mobile
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const canvas = canvasRef.current;
      if (!canvas || !floorMap) return;

      const rect = canvas.getBoundingClientRect();
      // In accessibility mode, the parent container has transform: scale(0.5)
      // So we need to adjust coordinates by multiplying by 2
      const accessibilityScale = state.isAccessibilityMode ? 2 : 1;
      const touchX = (touch.clientX - rect.left) * accessibilityScale;
      const touchY = (touch.clientY - rect.top) * accessibilityScale;

      // Check if clicking on a shop icon
      const scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9,
      );
      const totalScale = scale * scaleToFit;

      // Convert touch position to map coordinates
      const mapX = (touchX - canvas.width / 2 - pan.x) / totalScale;
      const mapY = (touchY - canvas.height / 2 - pan.y) / totalScale;

      // Check each shop, starting from topmost (front to back)
      // Sort by positionZ descending (highest Z first) to check topmost shops first
      const sortedShops = [...shops].sort(
        (a, b) => Number(b.positionZ || 0) - Number(a.positionZ || 0),
      );

      for (const shop of sortedShops) {
        if (!shop.mapIconInactiveUrl) continue;

        const icon = shopIcons.get(shop.id);
        if (!icon) continue;

        // Convert position values to numbers
        const posX =
          typeof shop.positionX === "number"
            ? shop.positionX
            : parseFloat(String(shop.positionX));
        const posY =
          typeof shop.positionY === "number"
            ? shop.positionY
            : parseFloat(String(shop.positionY));

        // Shop position in scaled coordinates
        const shopCanvasX = posX * 100;
        const shopCanvasY = posY * 100;

        // Icon size
        const scaleX =
          typeof shop.scaleX === "number"
            ? shop.scaleX
            : parseFloat(String(shop.scaleX || 1));
        const scaleY =
          typeof shop.scaleY === "number"
            ? shop.scaleY
            : parseFloat(String(shop.scaleY || 1));
        const iconWidth = icon.width * scaleX;
        const iconHeight = icon.height * scaleY;

        // Check if touch is within icon bounds (rectangular check)
        // Add a small tolerance (5% of icon size) to make clicking easier
        const tolerance = Math.max(iconWidth, iconHeight) * 0.05;
        const halfWidth = iconWidth / 2 + tolerance;
        const halfHeight = iconHeight / 2 + tolerance;
        const left = shopCanvasX - halfWidth;
        const right = shopCanvasX + halfWidth;
        const top = shopCanvasY - halfHeight;
        const bottom = shopCanvasY + halfHeight;

        if (mapX >= left && mapX <= right && mapY >= top && mapY <= bottom) {
          // Check if the pixel at touch position is opaque (not transparent)
          // But allow clicking even if check fails (fallback to rectangular area)
          const iconX = shopCanvasX - iconWidth / 2;
          const iconY = shopCanvasY - iconHeight / 2;
          let isOpaque = true; // Default to true

          try {
            isOpaque = isPixelOpaque(
              icon,
              iconX,
              iconY,
              iconWidth,
              iconHeight,
              mapX,
              mapY,
            );
          } catch (error) {
            // If opacity check fails, assume opaque to allow clicking
            isOpaque = true;
          }

          if (isOpaque) {
            // Touch start on shop: mark as potential click, allow drag-to-pan
            clickedOnShopRef.current = true;
            potentialShopRef.current = shop;
            hasMovedRef.current = false;
            setIsPanning(true);
            setLastMousePos({ x: touch.clientX, y: touch.clientY });
            return;
          }
        }
      }

      // No shop touched, start panning (don't clear selection yet)
      clickedOnShopRef.current = false;
      potentialShopRef.current = null;
      hasMovedRef.current = false;
      setIsPanning(true);
      setLastMousePos({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      // Start pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2),
      );
      setInitialPinchDistance(distance);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    if (e.touches.length === 1 && isPanning) {
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastMousePos.x;
      const deltaY = touch.clientY - lastMousePos.y;
      // Check if moved significantly (more than 3 pixels to avoid accidental triggers)
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        hasMovedRef.current = true;
      }
      setPan((prev) => clampPan(prev.x + deltaX, prev.y + deltaY));
      setLastMousePos({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      // Handle pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2),
      );

      if (initialPinchDistance > 0) {
        const scaleChange = currentDistance / initialPinchDistance;

        // Calculate center point between two touches
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;

        const rect = canvas.getBoundingClientRect();
        // In accessibility mode, the parent container has transform: scale(0.5)
        // So we need to adjust coordinates by multiplying by 2
        const accessibilityScale = state.isAccessibilityMode ? 2 : 1;
        const mouseX = (centerX - rect.left) * accessibilityScale;
        const mouseY = (centerY - rect.top) * accessibilityScale;

        setScale((prev) => {
          const newScale = prev * scaleChange;
          const clampedScale = Math.max(0.5, Math.min(5, newScale));

          if (clampedScale !== prev) {
            // Calculate pan adjustment to zoom towards center point
            const scaleToFit = Math.min(
              canvas.width / floorMap.width,
              canvas.height / floorMap.height,
              0.9,
            );

            // Point on image before zoom
            const imageX =
              (mouseX - canvas.width / 2 - pan.x) / (scaleToFit * prev);
            const imageY =
              (mouseY - canvas.height / 2 - pan.y) / (scaleToFit * prev);

            // Adjust pan to keep point under fingers fixed
            setPan((prevPan) => {
              const newPanX =
                prevPan.x - imageX * scaleToFit * (clampedScale - prev);
              const newPanY =
                prevPan.y - imageY * scaleToFit * (clampedScale - prev);
              return clampPan(newPanX, newPanY, clampedScale);
            });
          }

          return clampedScale;
        });
      }
      setInitialPinchDistance(currentDistance);
    }
  };

  const handleTouchEnd = () => {
    // If tap without movement on a shop, trigger onShopClick
    if (!hasMovedRef.current && potentialShopRef.current && onShopClick) {
      onShopClick(potentialShopRef.current);
    } else if (
      !hasMovedRef.current &&
      !clickedOnShopRef.current &&
      onClearSelection
    ) {
      // Only clear selection if it was a tap on empty space
      onClearSelection();
    }
    setIsPanning(false);
    hasMovedRef.current = false;
    clickedOnShopRef.current = false;
    potentialShopRef.current = null;
    setInitialPinchDistance(0);
  };

  const handleFloorClick = (floor: Floor) => {
    // Mark that this is a manual floor change
    isManualFloorChangeRef.current = true;
    setSelectedFloor(floor);
    if (state.currentFloor !== floor.number) {
      setCurrentFloor(floor.number);
    }
    setPan({ x: 0, y: 0 });
    setScale(DEFAULT_MAP_SCALE);
    // Clear pending center when user manually changes floor
    pendingCenterRef.current = null;
    // Reset flag after a short delay to allow floor change to complete
    // This prevents centering from happening during manual floor change
    setTimeout(() => {
      isManualFloorChangeRef.current = false;
    }, 300);
  };

  const handleGoToSelectedShopFloor = () => {
    if (!state.selectedShop) return;
    const targetFloor = floors.find(
      (floor) => floor.number === state.selectedShop!.floor,
    );
    if (targetFloor) handleFloorClick(targetFloor);
  };

  const handleCenterOnTerminalFromQuery = async () => {
    const params = new URLSearchParams(location.search);
    const terminalName = params.get("terminalName");
    if (!terminalName) return;
    try {
      const allTerminals = await apiService.getTerminals({ active: true });
      const target = allTerminals.find(
        (t) =>
          t.name.trim().toLowerCase() === terminalName.trim().toLowerCase(),
      );
      if (!target) return;

      // Map coordinates used in canvas drawing
      const mapX =
        (typeof target.positionX === "number"
          ? target.positionX
          : parseFloat(String(target.positionX))) * 100;
      const mapY =
        (typeof target.positionY === "number"
          ? target.positionY
          : parseFloat(String(target.positionY))) * 100;

      // Ensure correct floor is selected
      if (selectedFloor?.number !== target.floor) {
        const floor = floors.find((f) => f.number === target.floor);
        if (floor) {
          setSelectedFloor(floor);
          if (state.currentFloor !== floor.number)
            setCurrentFloor(floor.number);
          // Optionally adjust zoom for better visibility
          // setScale(1.5);
          // Defer centering until floorMap for the new floor is ready
          pendingCenterRef.current = { x: mapX, y: mapY };
          return;
        }
      }

      // Same floor: center immediately
      centerOnMapPoint(mapX, mapY);
      // setScale(1.5);
    } catch (e) {
      // Silently ignore if API fails
    }
  };

  // Check if we need to show floor change message
  const shouldShowFloorMessage = useMemo(() => {
    return (
      state.activeRoutePoints &&
      state.activeRoutePoints.length > 0 &&
      currentTerminal &&
      state.selectedShop &&
      state.currentFloor === currentTerminal.floor &&
      state.selectedShop.floor !== currentTerminal.floor
    );
  }, [
    state.activeRoutePoints,
    currentTerminal,
    state.selectedShop,
    state.currentFloor,
  ]);

  if (loading) {
    return (
      <div className={styles.mapContainer}>
        <div className={styles.loadingText}>
          {state.language === "en" ? "Loading map..." : "Загрузка карты..."}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mapContainer}>
      <div className={styles.floorSelector}>
        <div>
          {floors.map((floor) => {
            return (
              <button
                key={floor.id}
                className={`${styles.floorButton} ${
                  selectedFloor?.id === floor.id ? styles.floorButtonActive : ""
                }`}
                onClick={() => handleFloorClick(floor)}
              >
                {floor.number}
              </button>
            );
          })}
        </div>
        <div>
          {shouldShowFloorMessage && state.selectedShop && (
            <button
              type="button"
              className={`${styles.floorMessageButton} ${
                location.pathname === "/mobility"
                  ? styles.floorMessageMobility
                  : styles.floorMessage
              }`}
              onClick={handleGoToSelectedShopFloor}
            >
              {state.language === "en"
                ? `Go to floor ${state.selectedShop.floor}`
                : `Перейти на ${state.selectedShop.floor} этаж`}
            </button>
          )}
        </div>
        <div></div>
      </div>

      <div className={styles.clearSelectionButtons}>
        <button
          className={styles.iconButtonToTerminal}
          onClick={handleCenterOnTerminalFromQuery}
          title={state.language === "en" ? "To terminal" : "К терминалу"}
          aria-label={state.language === "en" ? "To terminal" : "К терминалу"}
        >
          {/* Location pin icon */}
          <svg
            width="100"
            height="100"
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M42.8553 89.3158C45.3129 91.3415 47.8761 93.193 50.5 95C53.1296 93.217 55.6803 91.3204 58.1447 89.3158C62.253 85.9458 66.1193 82.2918 69.7152 78.3807C78.0048 69.3256 87 56.1988 87 41.4635C87 36.675 86.0559 31.9335 84.2216 27.5095C82.3873 23.0856 79.6987 19.0659 76.3094 15.6799C72.9201 12.294 68.8963 9.60808 64.4679 7.77562C60.0396 5.94316 55.2932 5 50.5 5C45.7068 5 40.9604 5.94316 36.5321 7.77562C32.1037 9.60808 28.0799 12.294 24.6906 15.6799C21.3013 19.0659 18.6127 23.0856 16.7784 27.5095C14.9441 31.9335 14 36.675 14 41.4635C14 56.1988 22.9952 69.3216 31.2848 78.3807C34.8805 82.2932 38.7469 85.9445 42.8553 89.3158ZM50.5 54.6309C47.0043 54.6309 43.6518 53.2436 41.1799 50.7742C38.7081 48.3049 37.3194 44.9557 37.3194 41.4635C37.3194 37.9713 38.7081 34.6221 41.1799 32.1528C43.6518 29.6834 47.0043 28.2961 50.5 28.2961C53.9957 28.2961 57.3482 29.6834 59.8201 32.1528C62.2919 34.6221 63.6806 37.9713 63.6806 41.4635C63.6806 44.9557 62.2919 48.3049 59.8201 50.7742C57.3482 53.2436 53.9957 54.6309 50.5 54.6309Z"
              fill="#64A696"
            />
          </svg>
        </button>
        {/* Показываем кнопку "Маршрут с собой" только не на странице /mobility */}
        {state.activeRoutePoints &&
          state.activeRoutePoints.length > 0 &&
          location.pathname !== "/mobility" && (
            <button
              className={styles.iconButtonRouteQR}
              onClick={() => {
                setIsQRModalOpen(true);
              }}
              title={
                state.language === "en" ? "Route with you" : "Маршрут с собой"
              }
              aria-label={
                state.language === "en" ? "Route with you" : "Маршрут с собой"
              }
            >
              {/* QR code icon */}
              {/* <svg
                width="102"
                height="100"
                viewBox="0 0 102 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="84.5" cy="36.5" r="17.5" fill="white" />
                <path
                  d="M15 18.0455C15 15.2604 17.2604 13 20.0455 13H40.2273C43.0124 13 45.2727 15.2604 45.2727 18.0455V38.2273C45.2727 41.0124 43.0124 43.2727 40.2273 43.2727H20.0455C18.7073 43.2727 17.424 42.7412 16.4778 41.7949C15.5316 40.8487 15 39.5654 15 38.2273V18.0455ZM15 61.7727C15 58.9876 17.2604 56.7273 20.0455 56.7273H40.2273C43.0124 56.7273 45.2727 58.9876 45.2727 61.7727V81.9546C45.2727 84.7396 43.0124 87 40.2273 87H20.0455C18.7073 87 17.424 86.4684 16.4778 85.5222C15.5316 84.576 15 83.2927 15 81.9546V61.7727ZM58.7273 18.0455C58.7273 15.2604 60.9877 13 63.7728 13H83.9546C86.7397 13 89 15.2604 89 18.0455V38.2273C89 41.0124 86.7397 43.2727 83.9546 43.2727H63.7728C62.4346 43.2727 61.1513 42.7412 60.2051 41.7949C59.2589 40.8487 58.7273 39.5654 58.7273 38.2273V18.0455Z"
                  fill="url(#paint0_linear_5185_12210)"
                  stroke="url(#paint1_linear_5185_12210)"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M58.7266 58.4282H62.0902V61.7919H58.7266V58.4282ZM58.7266 81.935H62.0902V85.2986H58.7266V81.935ZM85.6357 58.4282H88.9993V61.7919H85.6357V58.4282ZM85.6357 81.935H88.9993V85.2986H85.6357V81.935ZM72.1811 70.1816H75.5448V73.5453H72.1811V70.1816Z"
                  stroke="url(#paint2_linear_5185_12210)"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_5185_12210"
                    x1="52"
                    y1="13"
                    x2="52"
                    y2="87"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#FAA30D" />
                    <stop offset="0.5" stop-color="#F76708" />
                    <stop offset="1" stop-color="#EB0C4F" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_5185_12210"
                    x1="52"
                    y1="13"
                    x2="52"
                    y2="87"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#FAA30D" />
                    <stop offset="0.5" stop-color="#F76708" />
                    <stop offset="1" stop-color="#EB0C4F" />
                  </linearGradient>
                  <linearGradient
                    id="paint2_linear_5185_12210"
                    x1="73.8629"
                    y1="58.4282"
                    x2="73.8629"
                    y2="85.2986"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#FAA30D" />
                    <stop offset="0.5" stop-color="#F76708" />
                    <stop offset="1" stop-color="#EB0C4F" />
                  </linearGradient>
                </defs>
              </svg> */}
              <svg
                width="102"
                height="100"
                viewBox="0 0 102 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="84.5" cy="36.5" r="17.5" fill="white" />
                <path
                  d="M15 18.0455C15 15.2604 17.2604 13 20.0455 13H40.2273C43.0124 13 45.2727 15.2604 45.2727 18.0455V38.2273C45.2727 41.0124 43.0124 43.2727 40.2273 43.2727H20.0455C18.7073 43.2727 17.424 42.7412 16.4778 41.7949C15.5316 40.8487 15 39.5654 15 38.2273V18.0455ZM15 61.7727C15 58.9876 17.2604 56.7273 20.0455 56.7273H40.2273C43.0124 56.7273 45.2727 58.9876 45.2727 61.7727V81.9546C45.2727 84.7396 43.0124 87 40.2273 87H20.0455C18.7073 87 17.424 86.4684 16.4778 85.5222C15.5316 84.576 15 83.2927 15 81.9546V61.7727ZM58.7273 18.0455C58.7273 15.2604 60.9877 13 63.7728 13H83.9546C86.7397 13 89 15.2604 89 18.0455V38.2273C89 41.0124 86.7397 43.2727 83.9546 43.2727H63.7728C62.4346 43.2727 61.1513 42.7412 60.2051 41.7949C59.2589 40.8487 58.7273 39.5654 58.7273 38.2273V18.0455Z"
                  fill="#1E6050"
                  stroke="#1E6050"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M58.7271 58.4282H62.0907V61.7919H58.7271V58.4282ZM58.7271 81.935H62.0907V85.2986H58.7271V81.935ZM85.6362 58.4282H88.9998V61.7919H85.6362V58.4282ZM85.6362 81.935H88.9998V85.2986H85.6362V81.935ZM72.1816 70.1816H75.5452V73.5453H72.1816V70.1816Z"
                  stroke="#1E6050"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        {/* Кнопка "Повторить маршрут" для страницы /mobility */}
        {location.pathname === "/mobility" &&
        state.activeRoutePoints &&
        state.activeRoutePoints.length > 0 ? (
          <button
            className={styles.repeatRouteButton}
            onClick={async () => {
              const params = new URLSearchParams(location.search);
              const terminalName = params.get("terminalName");
              const shopId = params.get("shopId");

              if (!terminalName || !shopId) return;

              try {
                // Находим стойку по имени
                const terminals = await apiService.getTerminals({
                  active: true,
                });
                const terminal = terminals.find((t) => t.name === terminalName);
                if (!terminal) return;

                // Находим объект по ID
                const shop = await apiService.getShopById(shopId);
                if (!shop) return;

                // Находим маршрут
                const routes: RouteEntity[] = await apiService.getRoutes();
                const match = routes.find(
                  (r) =>
                    Number(r.shopId) === Number(shop.id) &&
                    Number(r.terminalId) === Number(terminal.id),
                );

                if (match && match.points && match.points.length > 0) {
                  // Устанавливаем этаж стойки
                  if (terminal.floor && state.currentFloor !== terminal.floor) {
                    setCurrentFloor(terminal.floor);
                  }

                  // Устанавливаем выбранный объект
                  setSelectedShop(shop);
                  if (onShopClick) {
                    onShopClick(shop);
                  }

                  // Устанавливаем точки маршрута с задержкой
                  setTimeout(() => {
                    setActiveRoutePoints(match.points);
                  }, 100);
                }
              } catch (e) {
                console.error("Error repeating route:", e);
              }
            }}
            title={
              state.language === "en" ? "Repeat route" : "Повторить маршрут"
            }
            aria-label={
              state.language === "en" ? "Repeat route" : "Повторить маршрут"
            }
          >
            <svg
              width="100"
              height="100"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M25.5721 83.6689L29.6861 87.7575C30.6541 88.7196 31.1187 89.8419 31.08 91.1247C31.0412 92.4074 30.5766 93.5298 29.6861 94.4918C28.7181 95.4539 27.5694 95.9557 26.2401 95.9974C24.9107 96.0391 23.7604 95.5773 22.7892 94.6121L10.3264 82.2258C9.84244 81.7448 9.49881 81.2237 9.29553 80.6625C9.09226 80.1013 8.99384 79.5 9.0003 78.8586C9.00675 78.2173 9.10839 77.616 9.30521 77.0548C9.50204 76.4936 9.84405 75.9725 10.3313 75.4915L22.794 63.1052C23.762 62.1432 24.9123 61.683 26.2449 61.7247C27.5775 61.7664 28.7262 62.2666 29.6909 63.2255C30.5782 64.1875 31.0429 65.3099 31.0848 66.5926C31.1268 67.8753 30.6621 68.9977 29.6909 69.9598L25.577 74.0484H74.6972V59.6178C74.6972 58.2549 75.1619 57.1133 76.0911 56.193C77.0204 55.2726 78.1691 54.8108 79.5371 54.8076C80.9052 54.8044 82.0555 55.2662 82.988 56.193C83.9205 57.1197 84.3835 58.2613 84.3771 59.6178V74.0484C84.3771 76.6941 83.43 78.9597 81.536 80.8453C79.642 82.7309 77.3624 83.6721 74.6972 83.6689H25.5721ZM75.4232 25.9464H26.2981V40.377C26.2981 41.7399 25.8335 42.8831 24.9042 43.8067C23.975 44.7302 22.8263 45.1904 21.4582 45.1872C20.0901 45.184 18.9415 44.7222 18.0122 43.8019C17.0829 42.8815 16.6183 41.7399 16.6183 40.377V25.9464C16.6183 23.3008 17.5669 21.0368 19.4642 19.1544C21.3614 17.272 23.6394 16.3292 26.2981 16.326H75.4232L71.3093 12.2373C70.3413 11.2753 69.8767 10.1529 69.9154 8.87016C69.9541 7.58743 70.4188 6.46505 71.3093 5.50301C72.2773 4.54097 73.4259 4.04071 74.7553 4.00223C76.0847 3.96375 77.235 4.42392 78.2062 5.38276L90.6689 17.769C91.1529 18.2501 91.4966 18.7712 91.6998 19.3324C91.9031 19.8935 92.0032 20.4948 91.9999 21.1362C91.9967 21.7775 91.8967 22.3788 91.6998 22.94C91.503 23.5012 91.1594 24.0223 90.6689 24.5033L78.2062 36.8896C77.2382 37.8516 76.0895 38.3134 74.7602 38.2749C73.4308 38.2365 72.2805 37.7346 71.3093 36.7693C70.422 35.8073 69.9573 34.6849 69.9154 33.4022C69.8735 32.1195 70.3381 30.9971 71.3093 30.0351L75.4232 25.9464Z"
                fill="#64A696"
              />
            </svg>
          </button>
        ) : null}
        {/* Кнопка "Повторить маршрут" для других страниц (старая логика) */}
        {location.pathname !== "/mobility" &&
        state.selectedShopReload &&
        state.currentFloorReload !== undefined &&
        state.currentFloorReload !== state.currentFloor &&
        state.terminalName ? (
          <button
            className={styles.repeatRouteButton}
            onClick={async () => {
              if (!state.selectedShopReload || !state.terminalName) return;
              try {
                const terminals = await apiService.getTerminals({
                  active: true,
                });
                const terminal = terminals.find(
                  (t) => t.name === state.terminalName,
                );
                if (!terminal) return;
                const routes: RouteEntity[] = await apiService.getRoutes();
                const match = routes.find(
                  (r) =>
                    Number(r.shopId) === Number(state.selectedShopReload?.id) &&
                    Number(r.terminalId) === Number(terminal.id),
                );
                if (match && match.points && match.points.length > 0) {
                  // Update selected shop in global state first
                  setSelectedShop(state.selectedShopReload);
                  // Also call onShopClick if available to update local state in parent component
                  if (onShopClick && state.selectedShopReload) {
                    onShopClick(state.selectedShopReload);
                  }
                  // Set route points after selectedShop update
                  // Use setTimeout to ensure route is set after useEffect that clears route completes
                  // The useEffect in AppContext clears activeRoutePoints when selectedShop.id changes,
                  // so we need to set it after that effect has run
                  setTimeout(() => {
                    setActiveRoutePoints(match.points);
                  }, 50);
                  if (state.currentFloorReload !== undefined) {
                    setCurrentFloor(state.currentFloorReload);
                  }
                }
              } catch (e) {
                console.error("Error repeating route:", e);
              }
            }}
            title={
              state.language === "en" ? "Repeat route" : "Повторить маршрут"
            }
            aria-label={
              state.language === "en" ? "Repeat route" : "Повторить маршрут"
            }
          >
            <svg
              width="100"
              height="100"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M25.5721 83.6689L29.6861 87.7575C30.6541 88.7196 31.1187 89.8419 31.08 91.1247C31.0412 92.4074 30.5766 93.5298 29.6861 94.4918C28.7181 95.4539 27.5694 95.9557 26.2401 95.9974C24.9107 96.0391 23.7604 95.5773 22.7892 94.6121L10.3264 82.2258C9.84244 81.7448 9.49881 81.2237 9.29553 80.6625C9.09226 80.1013 8.99384 79.5 9.0003 78.8586C9.00675 78.2173 9.10839 77.616 9.30521 77.0548C9.50204 76.4936 9.84405 75.9725 10.3313 75.4915L22.794 63.1052C23.762 62.1432 24.9123 61.683 26.2449 61.7247C27.5775 61.7664 28.7262 62.2666 29.6909 63.2255C30.5782 64.1875 31.0429 65.3099 31.0848 66.5926C31.1268 67.8753 30.6621 68.9977 29.6909 69.9598L25.577 74.0484H74.6972V59.6178C74.6972 58.2549 75.1619 57.1133 76.0911 56.193C77.0204 55.2726 78.1691 54.8108 79.5371 54.8076C80.9052 54.8044 82.0555 55.2662 82.988 56.193C83.9205 57.1197 84.3835 58.2613 84.3771 59.6178V74.0484C84.3771 76.6941 83.43 78.9597 81.536 80.8453C79.642 82.7309 77.3624 83.6721 74.6972 83.6689H25.5721ZM75.4232 25.9464H26.2981V40.377C26.2981 41.7399 25.8335 42.8831 24.9042 43.8067C23.975 44.7302 22.8263 45.1904 21.4582 45.1872C20.0901 45.184 18.9415 44.7222 18.0122 43.8019C17.0829 42.8815 16.6183 41.7399 16.6183 40.377V25.9464C16.6183 23.3008 17.5669 21.0368 19.4642 19.1544C21.3614 17.272 23.6394 16.3292 26.2981 16.326H75.4232L71.3093 12.2373C70.3413 11.2753 69.8767 10.1529 69.9154 8.87016C69.9541 7.58743 70.4188 6.46505 71.3093 5.50301C72.2773 4.54097 73.4259 4.04071 74.7553 4.00223C76.0847 3.96375 77.235 4.42392 78.2062 5.38276L90.6689 17.769C91.1529 18.2501 91.4966 18.7712 91.6998 19.3324C91.9031 19.8935 92.0032 20.4948 91.9999 21.1362C91.9967 21.7775 91.8967 22.3788 91.6998 22.94C91.503 23.5012 91.1594 24.0223 90.6689 24.5033L78.2062 36.8896C77.2382 37.8516 76.0895 38.3134 74.7602 38.2749C73.4308 38.2365 72.2805 37.7346 71.3093 36.7693C70.422 35.8073 69.9573 34.6849 69.9154 33.4022C69.8735 32.1195 70.3381 30.9971 71.3093 30.0351L75.4232 25.9464Z"
                fill="#64A696"
              />
            </svg>
          </button>
        ) : null}
        {selectedShop && onClearSelection && (
          <button
            className={styles.clearSelectionButton}
            onClick={() => {
              onClearSelection();
              setSelectedShopReload(undefined);
              setCurrentFloorReload(undefined);
            }}
            title={
              state.language === "en" ? "Clear selection" : "Сбросить выбор"
            }
            aria-label={
              state.language === "en" ? "Clear selection" : "Сбросить выбор"
            }
          >
            <svg
              width="100"
              height="100"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M83 83L15 15M83 15L15 83"
                stroke="#EB0C4F"
                strokeWidth="10"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      <canvas
        ref={canvasRef}
        className={styles.canvas}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />

      {/* <div className={styles.instructions}>
        {state.language === "en"
          ? "Drag to pan | Wheel to zoom"
          : "Перетаскивание - панорамирование | Колесо мыши - масштаб"}
      </div> */}
      <RouteQRModal
        isOpen={isQRModalOpen}
        onClose={() => setIsQRModalOpen(false)}
        terminalName={
          new URLSearchParams(location.search).get("terminalName") ||
          state.terminalName ||
          null
        }
        shopId={state.selectedShop?.id}
      />
    </div>
  );
}
